<template>
  <HrPageLayout
    title="Attachees & Volunteers"
    subtitle="Departmental HR register → Director HR approval → deploy and reporting"
    icon="🤝"
    :breadcrumb-items="[{ label: 'Home', route: '/dashboard' }, { label: 'Attachees & Volunteers' }]"
  >
    <template #header-actions>
      <button v-if="canReport" type="button" class="hr-btn hr-btn-outline-success" :disabled="exporting" @click="exportCsv">
        <i class="bx bx-download"></i> CSV
      </button>
      <button v-if="canReport" type="button" class="hr-btn hr-btn-outline-success" :disabled="exportingPdf" @click="exportPdf">
        <i class="bx" :class="exportingPdf ? 'bx-loader-alt bx-spin' : 'bx-file'"></i> PDF
      </button>
      <button type="button" class="hr-btn hr-btn-primary" @click="openNew">
        <i class="bx bx-plus"></i> Register
      </button>
    </template>

    <div class="av-page">
      <!-- Director HR dashboard -->
      <div v-if="canApprove" class="av-director">
        <h4><i class="bx bx-shield-quarter"></i> Director HR — pending approval</h4>
        <p class="muted">Review submitted applications from departmental HR, then approve or reject.</p>
        <div v-if="!pendingDirector.length" class="muted">No pending submissions.</div>
        <div v-else class="av-table-wrap">
          <table class="av-table">
            <thead>
              <tr>
                <th>Name</th><th>Type</th><th>Department</th><th>Institution</th><th>Period</th><th>Submitted</th><th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="p in pendingDirector" :key="'p' + p.id">
                <td><strong>{{ p.full_name }}</strong></td>
                <td>{{ p.person_type }}</td>
                <td>{{ p.requesting_department || '—' }}</td>
                <td>{{ p.institution_name || '—' }}</td>
                <td>{{ p.period_from || '—' }} → {{ p.period_to || '—' }}</td>
                <td>{{ p.submitted_at || '—' }}</td>
                <td class="acts">
                  <button type="button" class="hr-btn hr-btn-sm" @click="openView(p)">Review</button>
                  <button type="button" class="hr-btn hr-btn-sm hr-btn-outline-success" @click="approve(p)">Approve</button>
                  <button type="button" class="hr-btn hr-btn-sm hr-btn-outline-danger" @click="reject(p)">Reject</button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="av-stats">
        <div class="av-stat" v-for="s in statCards" :key="s.key" @click="setStatusFilter(s.key)">
          <span>{{ s.label }}</span>
          <strong>{{ stats[s.field] || 0 }}</strong>
        </div>
      </div>

      <div v-if="(byDepartment || []).length" class="av-by-dept">
        <h5>By department</h5>
        <ul>
          <li v-for="d in byDepartment" :key="d.department_name">
            <strong>{{ d.department_name }}</strong> — {{ d.cnt }}
            (deployed {{ d.deployed_cnt }}, attachment {{ d.attachment_cnt }}, volunteer {{ d.volunteer_cnt }})
          </li>
        </ul>
      </div>

      <div class="av-filters">
        <label><span>Search</span><input v-model="filters.search" type="search" placeholder="Name, ID, mobile, institution…" @input="debouncedLoad"></label>
        <label>
          <span>Status</span>
          <select v-model="filters.status" @change="onFilterChange">
            <option value="">All</option>
            <option value="draft">Draft</option>
            <option value="submitted">Submitted (Director)</option>
            <option value="approved">Approved</option>
            <option value="deployed">Deployed</option>
            <option value="rejected">Rejected</option>
          </select>
        </label>
        <label>
          <span>Type</span>
          <select v-model="filters.person_type" @change="onFilterChange">
            <option value="">All</option>
            <option value="attachment">Attachment</option>
            <option value="volunteer">Volunteer</option>
          </select>
        </label>
        <label><span>Department</span><input v-model="filters.department" placeholder="Filter department" @change="onFilterChange"></label>
        <label>
          <span>Per page</span>
          <select v-model.number="pagination.limit" @change="onFilterChange">
            <option :value="10">10</option>
            <option :value="25">25</option>
            <option :value="50">50</option>
          </select>
        </label>
      </div>

      <div class="av-card">
        <div v-if="loading" class="av-loading"><div class="av-spin"></div> Loading…</div>
        <div v-else-if="!rows.length" class="av-empty">
          <i class="bx bx-user-plus"></i>
          <h5>No records yet</h5>
          <p>Use <strong>Register</strong> for full personal, academic, period and document capture, then submit to Director HR.</p>
          <button type="button" class="hr-btn hr-btn-primary" @click="openNew"><i class="bx bx-plus"></i> Register</button>
        </div>
        <div v-else class="av-table-wrap">
          <table class="av-table">
            <thead>
              <tr>
                <th>#</th><th>Name</th><th>ID</th><th>Mobile</th><th>Type</th><th>Department</th>
                <th>Period</th><th>Status</th><th>Deployment</th><th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(r, idx) in rows" :key="r.id">
                <td>{{ rowNum(idx) }}</td>
                <td><strong>{{ r.full_name }}</strong></td>
                <td class="mono">{{ r.national_id }}</td>
                <td class="mono">{{ r.mobile_number }}</td>
                <td><span class="pill">{{ r.person_type }}</span></td>
                <td>{{ r.requesting_department || '—' }}</td>
                <td>{{ r.period_from || '—' }} → {{ r.period_to || '—' }}</td>
                <td><span class="status" :class="r.status">{{ r.status }}</span></td>
                <td>{{ r.deployment_place || '—' }}</td>
                <td class="acts">
                  <button type="button" class="hr-btn hr-btn-sm" @click="openView(r)">Open</button>
                  <button v-if="canCreate && (r.status === 'draft' || r.status === 'rejected')" type="button" class="hr-btn hr-btn-sm hr-btn-primary" @click="submit(r)">Submit to Director</button>
                  <button v-if="canApprove && r.status === 'submitted'" type="button" class="hr-btn hr-btn-sm hr-btn-outline-success" @click="approve(r)">Approve</button>
                  <button v-if="canApprove && r.status === 'submitted'" type="button" class="hr-btn hr-btn-sm hr-btn-outline-danger" @click="reject(r)">Reject</button>
                  <button v-if="canCreate && (r.status === 'approved' || r.status === 'deployed')" type="button" class="hr-btn hr-btn-sm hr-btn-outline-primary" @click="openDeploy(r)">Deploy</button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-if="pagination.total" class="av-pager">
          <span>Showing {{ pageFrom }}–{{ pageTo }} of {{ pagination.total }}</span>
          <div>
            <button type="button" :disabled="!pagination.hasPrev" @click="goPage(pagination.page - 1)">Prev</button>
            <span>Page {{ pagination.page }}/{{ pagination.totalPages }}</span>
            <button type="button" :disabled="!pagination.hasNext" @click="goPage(pagination.page + 1)">Next</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Full register / edit modal -->
    <div v-if="showModal" class="modal-overlay" @click.self="showModal = false">
      <div class="modal-card wide">
        <div class="modal-head">
          <h3>{{ form.id ? 'Record #' + form.id : 'Register attachee / volunteer' }}</h3>
          <button type="button" class="x" @click="showModal = false">&times;</button>
        </div>
        <div class="modal-body">
          <div class="tabs">
            <button type="button" :class="{ on: tab === 'personal' }" @click="tab = 'personal'">1. Personal</button>
            <button type="button" :class="{ on: tab === 'academic' }" @click="tab = 'academic'">2. Academic</button>
            <button type="button" :class="{ on: tab === 'period' }" @click="tab = 'period'">3. Period</button>
            <button type="button" :class="{ on: tab === 'docs' }" @click="tab = 'docs'" :disabled="!form.id">4. Documents</button>
          </div>

          <div v-if="form.id" class="meta-box">
            <div><strong>Status:</strong> <span class="status" :class="detail.status || form.status">{{ detail.status || form.status || 'draft' }}</span></div>
            <div><strong>Registered by:</strong> {{ detail.registered_by || '—' }} · {{ detail.created_at || '—' }}</div>
            <div><strong>Approved by:</strong> {{ detail.approved_by || '—' }} · {{ detail.approved_at || '—' }}</div>
            <div v-if="detail.deployment_place"><strong>Deployed to:</strong> {{ detail.deployment_place }} ({{ detail.deployed_by || '—' }})</div>
            <div v-if="detail.rejection_reason" class="err"><strong>Rejection:</strong> {{ detail.rejection_reason }}</div>
          </div>

          <div v-show="tab === 'personal'" class="grid2">
            <label><span>Type *</span>
              <select v-model="form.person_type" :disabled="formLocked">
                <option value="attachment">Attachment (attachee)</option>
                <option value="volunteer">Volunteer</option>
              </select>
            </label>
            <label><span>Gender</span>
              <select v-model="form.gender" :disabled="formLocked">
                <option value="">—</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
              </select>
            </label>
            <label><span>First name *</span><input v-model="form.first_name" :disabled="formLocked"></label>
            <label><span>Middle name (optional)</span><input v-model="form.middle_name" :disabled="formLocked"></label>
            <label><span>Last name *</span><input v-model="form.last_name" :disabled="formLocked"></label>
            <label><span>National ID / Passport *</span><input v-model="form.national_id" :disabled="formLocked"></label>
            <label><span>Mobile *</span><input v-model="form.mobile_number" :disabled="formLocked"></label>
            <label><span>Email</span><input v-model="form.email" type="email" :disabled="formLocked"></label>
            <label><span>Date of birth</span><input v-model="form.date_of_birth" type="date" :disabled="formLocked"></label>
            <label><span>Disability (if any)</span><input v-model="form.disability" :disabled="formLocked"></label>
            <label><span>County</span><input v-model="form.county" :disabled="formLocked"></label>
            <label><span>Sub-county</span><input v-model="form.sub_county" :disabled="formLocked"></label>
            <label><span>Ward</span><input v-model="form.ward" :disabled="formLocked"></label>
            <label><span>Residence / estate</span><input v-model="form.residence" :disabled="formLocked"></label>
            <label class="span2"><span>Postal address</span><input v-model="form.postal_address" :disabled="formLocked"></label>
            <label><span>Next of kin name</span><input v-model="form.next_of_kin_name" :disabled="formLocked"></label>
            <label><span>Next of kin phone</span><input v-model="form.next_of_kin_phone" :disabled="formLocked"></label>
            <label><span>Relationship</span><input v-model="form.next_of_kin_relation" :disabled="formLocked"></label>
          </div>

          <div v-show="tab === 'academic'" class="grid2">
            <label class="span2"><span>Institution / college / university</span><input v-model="form.institution_name" :disabled="formLocked"></label>
            <label class="span2"><span>Course / programme</span><input v-model="form.course_programme" :disabled="formLocked"></label>
            <label><span>Education level</span><input v-model="form.education_level" placeholder="Certificate / Diploma / Degree…" :disabled="formLocked"></label>
            <label><span>Year of study</span><input v-model="form.year_of_study" :disabled="formLocked"></label>
            <label class="span2"><span>Highest qualification</span><input v-model="form.academic_qualification" :disabled="formLocked"></label>
          </div>

          <div v-show="tab === 'period'" class="grid2">
            <label><span>Period from *</span><input v-model="form.period_from" type="date" :disabled="formLocked"></label>
            <label><span>Period to *</span><input v-model="form.period_to" type="date" :disabled="formLocked"></label>
            <label class="span2"><span>Requesting department *</span><input v-model="form.requesting_department" :disabled="formLocked" placeholder="e.g. Health, Agriculture"></label>
            <label><span>Preferred station / facility</span><input v-model="form.preferred_station" :disabled="formLocked"></label>
            <label><span>Proposed supervisor</span><input v-model="form.supervisor_name" :disabled="formLocked"></label>
            <label class="span2"><span>Notes for HR</span><textarea v-model="form.notes" rows="3" :disabled="formLocked"></textarea></label>
          </div>

          <div v-show="tab === 'docs'" class="docs-block">
            <p class="muted">Upload supporting documents for HR processing. Recommended:</p>
            <ul class="req-docs">
              <li v-for="d in requiredDocs" :key="d">{{ d }}</li>
            </ul>
            <div v-if="canCreate && !formLockedStrict" class="doc-upload">
              <select v-model="docType">
                <option value="">Document type…</option>
                <option v-for="d in requiredDocs" :key="d" :value="d">{{ d }}</option>
                <option value="Other">Other</option>
              </select>
              <input v-if="docType === 'Other'" v-model="docTypeOther" type="text" placeholder="Specify type">
              <input type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" @change="onDocPick">
              <button type="button" class="hr-btn hr-btn-sm hr-btn-primary" :disabled="uploadingDoc || !docType || !docFile" @click="uploadDoc">Upload</button>
            </div>
            <ul class="doc-list">
              <li v-for="d in documents" :key="d.id">
                <div>
                  <strong>{{ d.document_type }}</strong>
                  <small>{{ d.original_name }} · {{ d.uploaded_by }} · {{ d.created_at }}</small>
                </div>
                <div class="doc-acts">
                  <button type="button" class="link" @click="downloadDoc(d)">Open</button>
                  <button v-if="canCreate && !formLockedStrict" type="button" class="link danger" @click="removeDoc(d)">Remove</button>
                </div>
              </li>
              <li v-if="!documents.length" class="muted">No documents yet — save personal/period first, then upload.</li>
            </ul>
            <div v-if="missingDocs.length" class="warn-box">Still recommended: {{ missingDocs.join('; ') }}</div>
          </div>
        </div>
        <div class="modal-actions">
          <button type="button" class="hr-btn hr-btn-secondary" @click="showModal = false">Close</button>
          <button v-if="canCreate && !formLocked && tab !== 'docs'" type="button" class="hr-btn" @click="nextTab">Next</button>
          <button v-if="canCreate && !formLocked" type="button" class="hr-btn hr-btn-primary" :disabled="saving" @click="saveForm">
            {{ form.id ? 'Save changes' : 'Save & continue to documents' }}
          </button>
          <button v-if="canCreate && form.id && (form.status === 'draft' || form.status === 'rejected' || detail.status === 'draft' || detail.status === 'rejected')" type="button" class="hr-btn hr-btn-outline-success" @click="submit(form)">
            Submit to Director HR
          </button>
        </div>
      </div>
    </div>

    <div v-if="deployTarget" class="modal-overlay" @click.self="deployTarget = null">
      <div class="modal-card small">
        <div class="modal-head">
          <h3>Deploy — {{ deployTarget.full_name }}</h3>
          <button type="button" class="x" @click="deployTarget = null">&times;</button>
        </div>
        <div class="modal-body grid2">
          <label class="span2"><span>Deployment place / unit *</span><input v-model="deployForm.place" placeholder="Facility, ward, office…"></label>
          <label class="span2"><span>Deployment department</span><input v-model="deployForm.department"></label>
          <label><span>Period from</span><input v-model="deployForm.period_from" type="date"></label>
          <label><span>Period to</span><input v-model="deployForm.period_to" type="date"></label>
          <label class="span2"><span>Notes</span><textarea v-model="deployForm.notes" rows="3"></textarea></label>
        </div>
        <div class="modal-actions">
          <button type="button" class="hr-btn hr-btn-secondary" @click="deployTarget = null">Cancel</button>
          <button type="button" class="hr-btn hr-btn-primary" :disabled="saving" @click="confirmDeploy">Confirm deployment</button>
        </div>
      </div>
    </div>
  </HrPageLayout>
</template>

<script>
/* eslint-disable */
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import { execute, executeDownload, authUrl, toast, getRight } from "@/api";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { addCountyLetterhead, loadCountyLogoDataUrl } from "@/utils/payrollReconPdf";

const emptyForm = () => ({
  id: null,
  person_type: "attachment",
  status: "draft",
  first_name: "",
  middle_name: "",
  last_name: "",
  full_name: "",
  national_id: "",
  mobile_number: "",
  email: "",
  gender: "",
  date_of_birth: "",
  disability: "",
  county: "",
  sub_county: "",
  ward: "",
  residence: "",
  postal_address: "",
  next_of_kin_name: "",
  next_of_kin_phone: "",
  next_of_kin_relation: "",
  institution_name: "",
  course_programme: "",
  education_level: "",
  year_of_study: "",
  academic_qualification: "",
  period_from: "",
  period_to: "",
  requesting_department: "",
  preferred_station: "",
  supervisor_name: "",
  notes: "",
});

export default {
  name: "AttacheesVolunteers",
  components: { HrPageLayout },
  data() {
    return {
      loading: false,
      saving: false,
      exporting: false,
      exportingPdf: false,
      uploadingDoc: false,
      rows: [],
      stats: {},
      pendingDirector: [],
      byDepartment: [],
      letterhead: null,
      filters: { search: "", status: "", person_type: "", department: "" },
      pagination: { page: 1, limit: 25, total: 0, totalPages: 1, hasPrev: false, hasNext: false },
      debounceTimer: null,
      showModal: false,
      tab: "personal",
      form: emptyForm(),
      detail: {},
      documents: [],
      requiredDocs: [
        "National ID / Passport copy",
        "Application / cover letter",
        "Institution introduction / attachment letter",
        "Academic credentials / student ID",
        "Curriculum Vitae (CV)",
      ],
      missingDocs: [],
      docType: "",
      docTypeOther: "",
      docFile: null,
      deployTarget: null,
      deployForm: { place: "", department: "", notes: "", period_from: "", period_to: "" },
      statCards: [
        { key: "", label: "Total", field: "total" },
        { key: "submitted", label: "With Director", field: "submitted_count" },
        { key: "approved", label: "Approved", field: "approved_count" },
        { key: "deployed", label: "Deployed", field: "deployed_count" },
        { key: "rejected", label: "Rejected", field: "rejected_count" },
      ],
      rights: { create: true, approve: true, report: true },
    };
  },
  computed: {
    canCreate() {
      return this.rights.create || getRight([
        "ATTACHEES VOLUNTEERS",
        "ATTACHEES VOLUNTEERS CREATE",
        "ATTACHEES VOLUNTEERS MANAGE",
        "USER MANAGEMENT",
        "CATEGORY & ROLES",
        "DEPARTMENT MANAGEMENT",
        "EMPLOYEE REGISTER",
      ]);
    },
    canApprove() {
      return this.rights.approve || getRight([
        "ATTACHEES VOLUNTEERS APPROVE",
        "ATTACHEES VOLUNTEERS DIRECTOR",
        "ATTACHEES VOLUNTEERS MANAGE",
        "DIRECTOR HR",
        "USER MANAGEMENT",
        "CATEGORY & ROLES",
      ]);
    },
    canReport() {
      return this.rights.report || this.canCreate || this.canApprove || getRight(["ATTACHEES VOLUNTEERS REPORTS"]);
    },
    formLocked() {
      const st = (this.detail.status || this.form.status || "draft").toLowerCase();
      if (!this.form.id) return false;
      if (this.canApprove) return false;
      return !this.canCreate || (st !== "draft" && st !== "rejected");
    },
    formLockedStrict() {
      const st = (this.detail.status || this.form.status || "draft").toLowerCase();
      return st === "deployed" && !this.canApprove;
    },
    pageFrom() {
      if (!this.pagination.total) return 0;
      return (this.pagination.page - 1) * this.pagination.limit + 1;
    },
    pageTo() {
      return Math.min(this.pagination.page * this.pagination.limit, this.pagination.total);
    },
  },
  mounted() {
    this.loadLetterhead();
    this.loadStats();
    this.loadData();
  },
  beforeUnmount() {
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
  },
  methods: {
    rowNum(i) {
      return (this.pagination.page - 1) * this.pagination.limit + i + 1;
    },
    setStatusFilter(k) {
      this.filters.status = k || "";
      this.onFilterChange();
    },
    debouncedLoad() {
      if (this.debounceTimer) clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        this.pagination.page = 1;
        this.loadData();
      }, 350);
    },
    onFilterChange() {
      this.pagination.page = 1;
      this.loadData();
    },
    goPage(p) {
      this.pagination.page = Math.max(1, p);
      this.loadData();
    },
    nextTab() {
      const order = ["personal", "academic", "period", "docs"];
      const i = order.indexOf(this.tab);
      if (i < order.length - 1) this.tab = order[i + 1];
    },
    d10(v) {
      return v ? String(v).slice(0, 10) : "";
    },
    async loadLetterhead() {
      try {
        const data = new FormData();
        data.append("function", "getCompanyInfo");
        const res = await execute(data, authUrl);
        if (res?.data?.success && res.data.data?.companyInfo) {
          const c = res.data.data.companyInfo;
          const abs = (u) => {
            if (!u) return "";
            if (/^https?:\/\//i.test(u)) return u;
            try {
              return new URL(u, authUrl).href;
            } catch (_) {
              return u;
            }
          };
          this.letterhead = {
            ...c,
            logoPrimaryUrl: abs(c.logoPrimaryUrl || c.logoPrimary),
            logoSecondaryUrl: abs(c.logoSecondaryUrl || c.logoSecondary),
          };
        }
      } catch (_) {
        this.letterhead = null;
      }
    },
    async loadStats() {
      try {
        const fd = new FormData();
        fd.append("function", "getAttacheeDirectorDashboard");
        if (this.filters.department) fd.append("department", this.filters.department);
        const res = await execute(fd, authUrl);
        if (res?.data?.success) {
          const d = res.data.data || {};
          this.stats = d.stats || {};
          this.pendingDirector = d.pending_director || [];
          this.byDepartment = d.by_department || [];
          if (Array.isArray(d.required_documents) && d.required_documents.length) {
            this.requiredDocs = d.required_documents;
          }
          this.rights = {
            create: d.can_create !== false,
            approve: !!d.can_approve,
            report: true,
          };
        }
      } catch (_) {
        this.stats = {};
      }
    },
    async loadData() {
      this.loading = true;
      try {
        const fd = new FormData();
        fd.append("function", "listAttacheeVolunteers");
        fd.append("page", String(this.pagination.page));
        fd.append("limit", String(this.pagination.limit));
        if (this.filters.search) fd.append("search", this.filters.search.trim());
        if (this.filters.status) fd.append("status", this.filters.status);
        if (this.filters.person_type) fd.append("person_type", this.filters.person_type);
        if (this.filters.department) fd.append("department", this.filters.department);
        const res = await execute(fd, authUrl);
        if (res?.data?.success) {
          const d = res.data.data || {};
          this.rows = d.records || [];
          const p = d.pagination || {};
          this.pagination = {
            page: Number(p.page || 1),
            limit: Number(p.limit || 25),
            total: Number(p.total || 0),
            totalPages: Number(p.totalPages || 1),
            hasPrev: !!p.hasPrev,
            hasNext: !!p.hasNext,
          };
        } else {
          this.rows = [];
          toast.error(res?.data?.message || "Failed to load — ensure Attachees roles are assigned, then re-login");
        }
      } catch (e) {
        this.rows = [];
      } finally {
        this.loading = false;
      }
    },
    openNew() {
      this.form = emptyForm();
      this.detail = {};
      this.documents = [];
      this.missingDocs = [];
      this.docType = "";
      this.docFile = null;
      this.tab = "personal";
      this.showModal = true;
    },
    mapRecord(r) {
      const f = emptyForm();
      Object.keys(f).forEach((k) => {
        if (r[k] != null) f[k] = r[k];
      });
      f.id = r.id;
      f.date_of_birth = this.d10(r.date_of_birth);
      f.period_from = this.d10(r.period_from);
      f.period_to = this.d10(r.period_to);
      return f;
    },
    async openView(r) {
      this.form = this.mapRecord(r);
      this.tab = "personal";
      this.docType = "";
      this.docFile = null;
      this.showModal = true;
      const fd = new FormData();
      fd.append("function", "getAttacheeVolunteer");
      fd.append("id", String(r.id));
      try {
        const res = await execute(fd, authUrl);
        if (res?.data?.success) {
          const rec = res.data.data.record || r;
          this.detail = rec;
          this.form = this.mapRecord(rec);
          this.documents = res.data.data.documents || [];
          this.missingDocs = res.data.data.missing_documents || [];
          if (Array.isArray(res.data.data.required_documents)) {
            this.requiredDocs = res.data.data.required_documents;
          }
        }
      } catch (_) {
        this.detail = r;
        this.documents = [];
      }
    },
    async saveForm() {
      if (!this.form.first_name?.trim() || !this.form.last_name?.trim()) {
        toast.error("First name and last name are required");
        this.tab = "personal";
        return;
      }
      if (!this.form.national_id || !this.form.mobile_number) {
        toast.error("National ID and mobile are required");
        this.tab = "personal";
        return;
      }
      if (!this.form.period_from || !this.form.period_to) {
        toast.error("Period from and to are required");
        this.tab = "period";
        return;
      }
      if (!this.form.requesting_department?.trim()) {
        toast.error("Requesting department is required");
        this.tab = "period";
        return;
      }
      this.saving = true;
      try {
        const fd = new FormData();
        fd.append("function", "saveAttacheeVolunteer");
        if (this.form.id) fd.append("id", String(this.form.id));
        Object.keys(this.form).forEach((k) => {
          if (k === "id" || k === "status") return;
          fd.append(k, this.form[k] == null ? "" : String(this.form[k]));
        });
        const res = await execute(fd, authUrl);
        if (res?.data?.success) {
          toast.success(res.data.message || "Saved");
          const id = res.data.data?.id || this.form.id;
          await this.loadData();
          await this.loadStats();
          if (id) {
            await this.openView({ id });
            this.tab = "docs";
          }
        } else toast.error(res?.data?.message || "Save failed");
      } finally {
        this.saving = false;
      }
    },
    async submit(r) {
      if (!window.confirm("Submit this application to Director HR for approval?")) return;
      const fd = new FormData();
      fd.append("function", "submitAttacheeVolunteer");
      fd.append("id", String(r.id));
      const res = await execute(fd, authUrl);
      if (res?.data?.success) {
        toast.success(res.data.message || "Submitted to Director HR");
        this.showModal = false;
        this.loadData();
        this.loadStats();
      } else toast.error(res?.data?.message || "Submit failed — upload documents first");
    },
    async approve(r) {
      if (!window.confirm("Approve this applicant?")) return;
      const fd = new FormData();
      fd.append("function", "approveAttacheeVolunteer");
      fd.append("id", String(r.id));
      const res = await execute(fd, authUrl);
      if (res?.data?.success) {
        toast.success("Approved — departmental HR can deploy");
        this.loadData();
        this.loadStats();
        if (this.showModal) this.openView(r);
      } else toast.error(res?.data?.message || "Approve failed");
    },
    async reject(r) {
      const reason = window.prompt("Rejection reason:") || "";
      if (!reason.trim()) {
        toast.error("Rejection reason is required");
        return;
      }
      const fd = new FormData();
      fd.append("function", "rejectAttacheeVolunteer");
      fd.append("id", String(r.id));
      fd.append("rejection_reason", reason);
      const res = await execute(fd, authUrl);
      if (res?.data?.success) {
        toast.success("Rejected");
        this.loadData();
        this.loadStats();
      } else toast.error(res?.data?.message || "Reject failed");
    },
    openDeploy(r) {
      this.deployTarget = r;
      this.deployForm = {
        place: r.deployment_place || r.preferred_station || "",
        department: r.deployment_department || r.requesting_department || "",
        notes: r.deployment_notes || "",
        period_from: this.d10(r.period_from),
        period_to: this.d10(r.period_to),
      };
    },
    async confirmDeploy() {
      if (!this.deployForm.place) {
        toast.error("Deployment place required");
        return;
      }
      this.saving = true;
      try {
        const fd = new FormData();
        fd.append("function", "deployAttacheeVolunteer");
        fd.append("id", String(this.deployTarget.id));
        fd.append("deployment_place", this.deployForm.place);
        fd.append("deployment_department", this.deployForm.department);
        fd.append("deployment_notes", this.deployForm.notes);
        if (this.deployForm.period_from) fd.append("period_from", this.deployForm.period_from);
        if (this.deployForm.period_to) fd.append("period_to", this.deployForm.period_to);
        const res = await execute(fd, authUrl);
        if (res?.data?.success) {
          toast.success("Deployed");
          this.deployTarget = null;
          this.loadData();
          this.loadStats();
        } else toast.error(res?.data?.message || "Deploy failed");
      } finally {
        this.saving = false;
      }
    },
    onDocPick(e) {
      this.docFile = e?.target?.files?.[0] || null;
    },
    async uploadDoc() {
      const type = this.docType === "Other" ? this.docTypeOther : this.docType;
      if (!this.form.id || !type || !this.docFile) {
        toast.error("Select document type and file");
        return;
      }
      this.uploadingDoc = true;
      try {
        const fd = new FormData();
        fd.append("function", "uploadAttacheeDocument");
        fd.append("attachee_id", String(this.form.id));
        fd.append("document_type", type);
        fd.append("document_file", this.docFile);
        const res = await execute(fd, authUrl);
        if (res?.data?.success) {
          toast.success("Document uploaded");
          this.docType = "";
          this.docTypeOther = "";
          this.docFile = null;
          await this.openView({ id: this.form.id });
          this.tab = "docs";
        } else toast.error(res?.data?.message || "Upload failed");
      } finally {
        this.uploadingDoc = false;
      }
    },
    async removeDoc(d) {
      if (!window.confirm("Remove this document?")) return;
      const fd = new FormData();
      fd.append("function", "deleteAttacheeDocument");
      fd.append("document_id", String(d.id));
      const res = await execute(fd, authUrl);
      if (res?.data?.success) this.openView({ id: this.form.id });
      else toast.error(res?.data?.message || "Remove failed");
    },
    async downloadDoc(d) {
      try {
        const fd = new FormData();
        fd.append("function", "downloadAttacheeDocument");
        fd.append("document_id", String(d.id));
        const response = await executeDownload(fd, authUrl);
        const blob = response?.data;
        if (!(blob instanceof Blob)) {
          toast.error("Could not open file");
          return;
        }
        const url = URL.createObjectURL(blob);
        window.open(url, "_blank");
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      } catch (_) {
        toast.error("Download failed");
      }
    },
    async fetchExportRows() {
      const fd = new FormData();
      fd.append("function", "listAttacheeVolunteers");
      fd.append("exportAll", "1");
      if (this.filters.search) fd.append("search", this.filters.search.trim());
      if (this.filters.status) fd.append("status", this.filters.status);
      if (this.filters.person_type) fd.append("person_type", this.filters.person_type);
      if (this.filters.department) fd.append("department", this.filters.department);
      const res = await execute(fd, authUrl);
      return res?.data?.success ? res.data.data?.records || [] : [];
    },
    async exportCsv() {
      this.exporting = true;
      try {
        const rows = await this.fetchExportRows();
        const headers = [
          "ID", "First", "Middle", "Last", "Full name", "ID No", "Mobile", "Email", "Type", "Status",
          "Department", "Institution", "Course", "Period from", "Period to", "Deployment", "Registered by", "Approved by",
        ];
        const lines = [headers.join(",")];
        rows.forEach((r) => {
          const cells = [
            r.id, r.first_name, r.middle_name, r.last_name, r.full_name, r.national_id, r.mobile_number, r.email,
            r.person_type, r.status, r.requesting_department, r.institution_name, r.course_programme,
            r.period_from, r.period_to, r.deployment_place, r.registered_by, r.approved_by,
          ].map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`);
          lines.push(cells.join(","));
        });
        const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `attachees-volunteers-${Date.now()}.csv`;
        a.click();
        URL.revokeObjectURL(a.href);
      } finally {
        this.exporting = false;
      }
    },
    async exportPdf() {
      this.exportingPdf = true;
      try {
        const rows = await this.fetchExportRows();
        const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
        const logoDataUrl = await loadCountyLogoDataUrl(this.letterhead);
        const startY = addCountyLetterhead(doc, {
          logoDataUrl,
          title: "Attachees & Volunteers Report",
          subtitle: `Generated ${new Date().toLocaleString()}`,
        });
        autoTable(doc, {
          startY: startY + 8,
          head: [["Name", "ID", "Type", "Dept", "Period", "Status", "Deployed to"]],
          body: rows.map((r) => [
            r.full_name || "",
            r.national_id || "",
            r.person_type || "",
            r.requesting_department || "",
            `${r.period_from || ""} – ${r.period_to || ""}`,
            r.status || "",
            r.deployment_place || "",
          ]),
          styles: { fontSize: 8, cellPadding: 3 },
          headStyles: { fillColor: [15, 118, 110] },
          margin: { left: 36, right: 36 },
        });
        doc.save("attachees-volunteers-report.pdf");
      } catch (e) {
        toast.error(e?.message || "PDF failed");
      } finally {
        this.exportingPdf = false;
      }
    },
  },
};
</script>

<style scoped>
.av-page { display: flex; flex-direction: column; gap: 16px; }
.av-director {
  background: linear-gradient(135deg, #ecfdf5, #f0fdfa);
  border: 1px solid #99f6e4;
  border-radius: 12px;
  padding: 16px;
}
.av-director h4 { margin: 0 0 6px; color: #0f766e; }
.av-stats { display: grid; grid-template-columns: repeat(auto-fill, minmax(130px, 1fr)); gap: 10px; }
.av-stat {
  background: #fff; border: 1px solid #e2e8f0; border-radius: 10px; padding: 12px; cursor: pointer;
}
.av-stat span { display: block; font-size: 0.8rem; color: #64748b; }
.av-stat strong { font-size: 1.35rem; color: #0f172a; }
.av-by-dept { background: #fff; border: 1px solid #e2e8f0; border-radius: 10px; padding: 12px 16px; }
.av-by-dept ul { margin: 8px 0 0; padding-left: 18px; }
.av-filters { display: flex; flex-wrap: wrap; gap: 12px; }
.av-filters label { display: flex; flex-direction: column; gap: 4px; font-size: 0.8rem; font-weight: 600; color: #334155; }
.av-filters input, .av-filters select { padding: 6px 10px; border: 1px solid #cbd5e1; border-radius: 6px; font-weight: 400; min-width: 140px; }
.av-card { background: #fff; border: 1px solid #e2e8f0; border-radius: 12px; padding: 12px; }
.av-table-wrap { overflow-x: auto; }
.av-table { width: 100%; border-collapse: collapse; font-size: 0.9rem; }
.av-table th, .av-table td { padding: 8px 10px; border-bottom: 1px solid #f1f5f9; text-align: left; vertical-align: top; }
.av-table th { font-size: 0.75rem; text-transform: uppercase; color: #64748b; }
.mono { font-family: ui-monospace, monospace; font-size: 0.85rem; }
.pill { background: #e0f2fe; color: #0369a1; padding: 2px 8px; border-radius: 999px; font-size: 0.75rem; }
.status { text-transform: capitalize; font-weight: 600; font-size: 0.8rem; }
.status.submitted { color: #b45309; }
.status.approved { color: #047857; }
.status.deployed { color: #1d4ed8; }
.status.rejected { color: #b91c1c; }
.status.draft { color: #64748b; }
.acts { display: flex; flex-wrap: wrap; gap: 4px; }
.av-pager { display: flex; justify-content: space-between; align-items: center; margin-top: 12px; font-size: 0.85rem; }
.av-empty { text-align: center; padding: 40px 16px; color: #64748b; }
.av-empty i { font-size: 42px; color: #94a3b8; }
.av-loading { padding: 24px; text-align: center; color: #64748b; }
.modal-overlay {
  position: fixed; inset: 0; background: rgba(15, 23, 42, 0.45); z-index: 9000;
  display: flex; align-items: center; justify-content: center; padding: 16px;
}
.modal-card {
  background: #fff; border-radius: 12px; width: 100%; max-width: 520px; max-height: 92vh;
  overflow: auto; box-shadow: 0 20px 50px rgba(0, 0, 0, 0.2);
}
.modal-card.wide { max-width: 820px; }
.modal-card.small { max-width: 480px; }
.modal-head {
  display: flex; justify-content: space-between; align-items: center;
  padding: 14px 18px; border-bottom: 1px solid #e2e8f0;
}
.modal-head h3 { margin: 0; font-size: 1.05rem; }
.modal-head .x { border: 0; background: none; font-size: 1.5rem; cursor: pointer; line-height: 1; }
.modal-body { padding: 16px 18px; }
.modal-actions {
  display: flex; flex-wrap: wrap; gap: 8px; justify-content: flex-end;
  padding: 12px 18px; border-top: 1px solid #e2e8f0; background: #f8fafc;
}
.tabs { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 14px; }
.tabs button {
  border: 1px solid #cbd5e1; background: #fff; border-radius: 8px; padding: 6px 12px;
  font-size: 0.85rem; cursor: pointer;
}
.tabs button.on { background: #0f766e; color: #fff; border-color: #0f766e; }
.grid2 { display: grid; grid-template-columns: 1fr 1fr; gap: 10px 14px; }
@media (max-width: 640px) { .grid2 { grid-template-columns: 1fr; } .span2 { grid-column: span 1; } }
.span2 { grid-column: span 2; }
.grid2 label { display: flex; flex-direction: column; gap: 4px; font-size: 0.8rem; font-weight: 600; color: #334155; }
.grid2 input, .grid2 select, .grid2 textarea {
  padding: 7px 10px; border: 1px solid #cbd5e1; border-radius: 6px; font-weight: 400; font-size: 0.9rem;
}
.meta-box {
  background: #f8fafc; border-radius: 8px; padding: 10px 12px; margin-bottom: 12px;
  font-size: 0.88rem; display: grid; gap: 4px;
}
.meta-box .err { color: #b91c1c; }
.docs-block h4 { margin: 0 0 8px; }
.req-docs { margin: 0 0 10px; padding-left: 18px; font-size: 0.85rem; color: #475569; }
.doc-upload { display: flex; flex-wrap: wrap; gap: 8px; align-items: center; margin-bottom: 12px; }
.doc-list { list-style: none; margin: 0; padding: 0; }
.doc-list li {
  display: flex; justify-content: space-between; gap: 10px; padding: 8px 0;
  border-bottom: 1px solid #f1f5f9; font-size: 0.88rem;
}
.doc-list small { display: block; color: #94a3b8; }
.doc-acts { display: flex; gap: 8px; }
.link { background: none; border: 0; color: #0f766e; cursor: pointer; font-weight: 600; }
.link.danger { color: #b91c1c; }
.warn-box { background: #fffbeb; border: 1px solid #fcd34d; color: #92400e; padding: 8px 10px; border-radius: 8px; font-size: 0.85rem; margin-top: 8px; }
.muted { color: #64748b; font-size: 0.9rem; }
</style>
